import { useEffect, useRef } from 'react';
import { scrollIntoView as defaultScrollIntoView } from '@packages/shared/core/common/utils/scroll/scrollIntoView';

export type ScrollFunction = (elementId: string) => void;

export type UseScrollToParams = {
  shouldScroll: boolean;
  elementId: string;
  timeoutInMs?: number;
  onScroll?: () => void;
  scrollFn?: ScrollFunction;
};

export const useScrollTo = ({
  shouldScroll,
  elementId,
  timeoutInMs = 1000,
  onScroll = () => {},
  scrollFn = defaultScrollIntoView,
}: UseScrollToParams): void => {
  const hasScrolledRef = useRef(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!shouldScroll || !elementId || hasScrolledRef.current) {
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      scrollFn(elementId);
      onScroll();
      hasScrolledRef.current = true;
      timerRef.current = null;
    }, timeoutInMs);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [shouldScroll, elementId, timeoutInMs, onScroll, scrollFn]);
};
